import { useState, useEffect } from 'react';
import { searchStock } from '../../../constant/Constant';
import { HitApi } from '../../../Api/ApiHit';
import { getSelectedUnit } from '../../../storage/Storage';

const StockDashboard = () => {
  const [stockData, setStockData] = useState([]);
  const [warehouseStats, setWarehouseStats] = useState(null);
  const [salonStats, setSalonStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('warehouse'); // 'warehouse' or 'salon'

  const calculateWarehouseStats = (data) => {
    // Filter warehouse stock only
    const warehouseStock = data.filter(item => item.stockLocation === 'warehouse');
    
    let totalProducts = 0;
    let totalStockIn = 0;
    let totalStockOut = 0;
    let totalCurrentStock = 0;
    let totalValue = 0;
    let lowStockCount = 0;
    let outOfStockCount = 0;
    let professionalCount = 0;
    let retailCount = 0;

    warehouseStock.forEach(item => {
      const currentStock = (item.stockIn || 0) - (item.stockOut || 0);
      const costPrice = item.productId?.costPrice || 0;
      
      totalProducts++;
      totalStockIn += item.stockIn || 0;
      totalStockOut += item.stockOut || 0;
      totalCurrentStock += currentStock;
      totalValue += currentStock * costPrice;
      
      // Count low stock and out of stock
      if (currentStock === 0) {
        outOfStockCount++;
      } else if (currentStock <= (item.stockAlert || 5)) {
        lowStockCount++;
      }
      
      // Count by type
      if (item.type === 'professional') {
        professionalCount++;
      } else if (item.type === 'retail') {
        retailCount++;
      }
    });

    setWarehouseStats({
      totalProducts,
      totalStockIn,
      totalStockOut,
      totalCurrentStock,
      totalValue,
      lowStockCount,
      outOfStockCount,
      professionalCount,
      retailCount,
      warehouseStock
    });
  };

  const calculateSalonStats = (data) => {
    // Filter salon stock only
    const salonStock = data.filter(item => item.stockLocation === 'salon');
    
    let totalProducts = 0;
    let totalStockIn = 0;
    let totalStockOut = 0;
    let totalCurrentStock = 0;
    let totalValue = 0;
    let lowStockCount = 0;
    let outOfStockCount = 0;
    let professionalCount = 0;
    let retailCount = 0;

    salonStock.forEach(item => {
      const currentStock = (item.stockIn || 0) - (item.stockOut || 0);
      const costPrice = item.productId?.costPrice || 0;
      
      totalProducts++;
      totalStockIn += item.stockIn || 0;
      totalStockOut += item.stockOut || 0;
      totalCurrentStock += currentStock;
      totalValue += currentStock * costPrice;
      
      // Count low stock and out of stock
      if (currentStock === 0) {
        outOfStockCount++;
      } else if (currentStock <= (item.stockAlert || 5)) {
        lowStockCount++;
      }
      
      // Count by type
      if (item.type === 'professional') {
        professionalCount++;
      } else if (item.type === 'retail') {
        retailCount++;
      }
    });

    setSalonStats({
      totalProducts,
      totalStockIn,
      totalStockOut,
      totalCurrentStock,
      totalValue,
      lowStockCount,
      outOfStockCount,
      professionalCount,
      retailCount,
      salonStock
    });
  };

  const fetchStockData = async () => {
    try {
      setLoading(true);
      const selectedUnit = getSelectedUnit();
      console.log('Selected Unit:', selectedUnit);
      console.log('Unit ID:', selectedUnit?._id);
      console.log('API URL:', searchStock);
      
      const searchObj = {
        unitId: selectedUnit?._id,
        isActive: true
      };

      const payload = {
        page: 1,
        limit: 100, // Get more items for dashboard
        search: searchObj,
        populate: ['productId'] // Ensure product data is populated
      };
      console.log('API Payload:', payload);
      
      const response = await HitApi(payload, searchStock);
      console.log('API Response:', response);
      
      if (response?.statusCode === 200) {
        const stockData = response?.data?.docs || response?.stocks || response?.result || response?.body || [];
        setStockData(stockData);
        console.log('Stock Data Set:', stockData);
        
        // Calculate both warehouse and salon statistics
        calculateWarehouseStats(stockData);
        calculateSalonStats(stockData);
      } else {
        console.log('API Response Status:', response?.statusCode);
      }
    } catch (error) {
      console.error('Error fetching stock data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('StockDashboard component mounted');
    const selectedUnit = getSelectedUnit();
    console.log('useEffect - Selected Unit:', selectedUnit);
    if (selectedUnit?._id) {
      console.log('Calling fetchStockData...');
      fetchStockData();
    } else {
      console.log('No unit selected or unit ID missing');
    }
  }, []);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '' || isNaN(amount)) {
      return '₹0.00';
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '₹0.00';
    return `₹${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading warehouse dashboard...</div>
      </div>
    );
  }

  const activeStats = activeView === 'warehouse' ? warehouseStats : salonStats;
  const activeStockData = activeStats ? (activeView === 'warehouse' ? activeStats.warehouseStock : activeStats.salonStock) : [];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Stock Dashboard</h1>
            <p className="text-gray-600">Overview of {activeView} stock and inventory</p>
          </div>
          
          {/* Toggle Switch */}
          <div className="flex bg-gray-200 rounded-lg p-1">
            <button
              onClick={() => setActiveView('warehouse')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === 'warehouse'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Warehouse
            </button>
            <button
              onClick={() => setActiveView('salon')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === 'salon'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Salon
            </button>
          </div>
        </div>
      </div>

      {activeStats && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Products */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <span className="text-2xl">📦</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Products</p>
                  <p className="text-2xl font-bold text-gray-900">{activeStats.totalProducts}</p>
                </div>
              </div>
            </div>

            {/* Total Stock In */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <span className="text-2xl">📥</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Stock In</p>
                  <p className="text-2xl font-bold text-green-600">{activeStats.totalStockIn}</p>
                </div>
              </div>
            </div>

            {/* Total Stock Out */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <span className="text-2xl">📤</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Stock Out</p>
                  <p className="text-2xl font-bold text-orange-600">{activeStats.totalStockOut}</p>
                </div>
              </div>
            </div>

            {/* Current Stock */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <span className="text-2xl">📊</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Current Stock</p>
                  <p className="text-2xl font-bold text-purple-600">{activeStats.totalCurrentStock}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Value and Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* Total Value */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <span className="text-2xl">💰</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Stock Value</p>
                  <p className="text-2xl font-bold text-yellow-600">{formatCurrency(activeStats.totalValue)}</p>
                </div>
              </div>
            </div>

            {/* Out of Stock */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-lg">
                  <span className="text-2xl">⚠️</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                  <p className="text-2xl font-bold text-red-600">{activeStats.outOfStockCount}</p>
                </div>
              </div>
            </div>

            {/* Low Stock */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <span className="text-2xl">📉</span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Low Stock</p>
                  <p className="text-2xl font-bold text-amber-600">{activeStats.lowStockCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Type Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Type Distribution</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Professional Products</span>
                  <span className="font-bold text-purple-600">{activeStats.professionalCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Retail Products</span>
                  <span className="font-bold text-blue-600">{activeStats.retailCount}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Stock Health</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Healthy Stock</span>
                  <span className="font-bold text-green-600">
                    {activeStats.totalProducts - activeStats.outOfStockCount - activeStats.lowStockCount}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Needs Attention</span>
                  <span className="font-bold text-red-600">
                    {activeStats.outOfStockCount + activeStats.lowStockCount}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Warehouse Stock Details */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{activeView === 'warehouse' ? 'Warehouse' : 'Salon'} Stock Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock In</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Out</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeStockData.map((item, index) => {
                    const currentStock = (item.stockIn || 0) - (item.stockOut || 0);
                    const value = currentStock * (item.productId?.costPrice || 0);
                    const isOutOfStock = currentStock === 0;
                    const isLowStock = currentStock <= (item.stockAlert || 5) && currentStock > 0;
                    
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {item.productId?.productName || 'Unknown Product'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {item.productId?.brand || 'No Brand'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.type === 'professional' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {item.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                          {item.stockIn || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600">
                          {item.stockOut || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${
                            isOutOfStock ? 'text-red-600' : 
                            isLowStock ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {currentStock}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(value)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            isOutOfStock ? 'bg-red-100 text-red-800' :
                            isLowStock ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {isOutOfStock ? 'Out of Stock' : 
                             isLowStock ? 'Low Stock' : 'In Stock'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!activeStats && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">No {activeView} data available</p>
        </div>
      )}
    </div>
  );
};

export default StockDashboard;